import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Stack,
  Divider,
  Paper,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckIcon,
  Print as PrintIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
  AccountBalance as TransferIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import OnGeneralMessageDialog from '../../../../components/Dialogs/OnGeneralMessageDialog';
import { impresionrecibopagoinicial } from '../pagos/imprimirpagos';

const currencyRD = (v) => {
  if (typeof v === 'string') {
    const numValue = parseFloat(v.replace(/,/g, ''));
    if (isNaN(numValue)) return 'RD$0.00';
    return new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(numValue);
  }
  if (typeof v === 'number' && !isNaN(v)) {
    return new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(v);
  }
  return 'RD$0.00';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getPaymentMethodIcon = (method) => {
  switch (method) {
    case 'efectivo':
      return <MoneyIcon sx={{ color: '#2e7d32' }} />;
    case 'tarjeta':
      return <CardIcon sx={{ color: '#1976d2' }} />;
    case 'transferencia':
      return <TransferIcon sx={{ color: '#0288d1' }} />;
    default:
      return <MoneyIcon sx={{ color: '#666' }} />;
  }
};

const getPaymentMethodColor = (method) => {
  switch (method) {
    case 'efectivo':
      return 'success';
    case 'tarjeta':
      return 'primary';
    case 'transferencia':
      return 'info';
    default:
      return 'default';
  }
};

export default function DetalleInicialDialog({ open, onClose, inicial, onCancelPayment }) {
  const [printLoading, setPrintLoading] = useState(false);
  const [openCancelConfirmDialog, setOpenCancelConfirmDialog] = useState(false);

  if (!inicial) return null;


  const handleCancelPaymentRequest = () => {
    setOpenCancelConfirmDialog(true);
  }

  const handleCancelPaymentConfirm = () => {
    setOpenCancelConfirmDialog(false);
    onCancelPayment(inicial);
  }

  const handleCloseCancelConfirmDialog = () => {
    setOpenCancelConfirmDialog(false);
  }
  const imprimirRecibo = () => {
    setPrintLoading(true);
    impresionrecibopagoinicial(inicial.nombre_cliente,localStorage.getItem('nombre_compania'),"Pago de Inicial",new Date().toLocaleDateString(),inicial.monto_inicial,inicial.id_producto,[{concepto:"Inicial",total:inicial.monto_inicial}]);
    setPrintLoading(false);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1, position: 'relative' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              width: 48,
              height: 48
            }}
          >
            <ReceiptIcon />
          </Avatar>
          <Box> 
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Detalle del Pago Inicial
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Información completa del pago inicial procesado
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {/* Payment Status Card */}
          <Card sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CheckIcon sx={{ color: '#2e7d32', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" fontWeight={600} color="success.main">
                    Pago Inicial Procesado Exitosamente
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    El pago inicial ha sido registrado y confirmado
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Main Payment Information */}
          <Grid container spacing={3}>
            {/* Left Column - Payment Details */}
            <Grid item size={6}>
              <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon color="primary" />
                  Información del Pago
                </Typography>
                
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Monto del Inicial
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {currencyRD(inicial.monto_inicial)}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Fecha de Pago
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CalendarIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body1" fontWeight={500}>
                        {formatDate(inicial.fecha_creacion)}
                      </Typography>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Estado
                    </Typography>
                    <Chip
                      label={inicial.estado}
                      color={inicial.estado == "cancelado" ? "error" : inicial.estado == "pendiente" ? "warning" : "success"}
                      icon={inicial.estado == "cancelado" ? <CancelIcon /> : inicial.estado == "pendiente" ? <PendingIcon /> : <CheckIcon />}       
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Right Column - Client Information */}
            <Grid item size={6}>
              <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  Información del Cliente
                </Typography>
                
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Nombre del Cliente
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ width: 32, height: 32, backgroundColor: '#e3f2fd' }}>
                        <PersonIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body1" fontWeight={500}>
                        {inicial.nombre_cliente || 'N/A'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Identificación
                    </Typography>
                    <Typography variant="body1" fontWeight={500} fontFamily="monospace">
                      {inicial.identificacion || 'N/A'}
                    </Typography>
                  </Box>

                  <Divider />

                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Payment Methods Section */}
          {inicial.metodo_pago && (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceIcon color="primary" />
                Métodos de Pago Utilizados
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Método</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Monto</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Referencia</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {JSON.parse(inicial.metodo_pago).map((payment, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {getPaymentMethodIcon(payment.method)}
                            <Typography variant="body2" fontWeight={500}>
                              {payment.method.charAt(0).toUpperCase() + payment.method.slice(1)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {currencyRD(payment.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {payment.reference || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Additional Information */}
          {inicial.descripcion && (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptIcon color="primary" />
                Información Adicional
              </Typography>
              
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Descripción
                </Typography>
                <Typography variant="body1" sx={{ 
                  backgroundColor: '#f5f5f5', 
                  p: 2, 
                  borderRadius: 1,
                  fontStyle: 'italic'
                }}>
                  {inicial.descripcion}
                </Typography>
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'space-between' }}>
        {inicial.estado != "cancelado" && (
        <Button
          onClick={() => imprimirRecibo()}
          variant="outlined"
          color="primary"
          startIcon={<PrintIcon />}
          disabled={printLoading}
          sx={{
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderColor: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.light',
              color: 'primary.dark'
            }
          }}
        >
          {printLoading ? 'Generando...' : 'Imprimir Recibo'}
        </Button>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Cerrar
          </Button>
          {inicial.estado == "pendiente" && (
          <Button
            onClick={handleCancelPaymentRequest}
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            sx={{
              fontWeight: "bold",
              px: 4,
              py: 1.5,
              borderColor: 'error.main',
              '&:hover': {
                borderColor: 'error.dark',
                backgroundColor: 'error.light',
                color: 'error.dark'
              }
            }}
          >
            Cancelar Inicial
          </Button>
          )}
        </Box>
      </DialogActions>

      {/* Confirmation Dialog */}
      <OnGeneralMessageDialog
        open={openCancelConfirmDialog}
        onClose={handleCloseCancelConfirmDialog}
        title="¿Estás seguro de cancelar este inicial?"
        message="Esta acción cancelará permanentemente el pago inicial. Esta operación no se puede deshacer. ¿Deseas continuar?"
        buttonText="Sí, Cancelar Inicial"
        onRegister={handleCancelPaymentConfirm}
        buttonColor="error"
      />
    </Dialog>
  );
}
